<template>
  <v-row no-gutters style="background-color: #0f0f0f; min-height: 100vh">
    <v-col
      cols="12"
      md="10"
      lg="8"
      xl="6"
      class="mx-auto pa-4 pa-sm-8 mb-16 mb-sm-0"
    >
      <div class="d-flex align-center mb-6">
        <v-btn 
          icon 
          dark 
          class="mr-2" 
          @click="$router.push({ name: rutaInventario('listado') })"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="grey--text text-overline leading-none font-weight-black" style="font-size: 10px !important;">
            INVENTARIOS / EN CURSO
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn 
          text 
          x-small 
          color="blue lighten-2" 
          class="font-weight-black"
          @click="$router.push({ name: rutaInventario('stock') })"
        >
          <v-icon x-small class="mr-1">mdi-pause</v-icon> PAUSAR Y SALIR
        </v-btn>
      </div>

      <div class="d-flex align-start justify-space-between mb-8" v-if="inventario">
        <div class="flex-grow-1">
          <h1 class="text-h4 text-sm-h3 white--text font-weight-black tracking-tighter">
            Informe de Stock
          </h1>
          <div class="d-flex align-center mt-2">
            <v-chip
              x-small
              color="blue"
              label
              class="mr-3 font-weight-black white--text rounded-sm"
              >LIVE</v-chip
            >
            <span class="grey--text text-caption font-weight-bold uppercase letter-spacing-1">
              Iniciado: {{ moment(inventario.fecha_inicio).format("DD MMM, HH:mm") }}
            </span>
          </div>
        </div>

        <v-btn
          outlined
          dark
          color="grey darken-3"
          class="rounded-lg px-3 mr-2"
          :loading="descargandoHoja"
          @click="descargarHojaConteo"
        >
          <v-icon small color="grey lighten-1" class="mr-sm-1">mdi-printer-outline</v-icon>
          <span class="d-none d-sm-inline grey--text text--lighten-1 font-weight-bold">Hoja de conteo</span>
        </v-btn>

        <v-btn 
          outlined 
          dark 
          color="grey darken-3" 
          class="rounded-lg px-2"
          @click="abrirModalEliminar"
        >
          <v-icon small color="red lighten-2">mdi-delete-outline</v-icon>
        </v-btn>
      </div>

      <div class="bg-dark-refined pa-5 rounded-xl border-subtle mb-8 shadow-xl">
        <div class="d-flex justify-space-between align-center mb-3">
          <div class="d-flex align-center">
            <div class="pulse-blue mr-2"></div>
            <span class="white--text font-weight-black text-body-2 uppercase">Progreso de Auditoría</span>
          </div>
          <span class="blue--text font-weight-black text-h6">{{ progresoPorcentaje }}%</span>
        </div>
        <v-progress-linear
          :value="progresoPorcentaje"
          color="blue"
          height="8"
          rounded
          background-color="#252525"
        ></v-progress-linear>
      </div>

      <div class="white--text text-overline mb-3 grey--text font-weight-bold pl-2">
        Categorías por completar
      </div>

      <div class="rounded-xl border-subtle overflow-hidden mb-8 shadow-2xl">
        <v-list dark background-color="#161616" class="pa-0">
          <template v-for="(categoria, index) in categorias">
            <v-list-item
              :key="categoria.id"
              @click="irCategoria(categoria)"
              class="py-4 yt-task-item"
              :class="{ 'item-done-bg': categoria.completada }"
            >
              <v-list-item-action class="mr-4 ml-2">
                <v-avatar
                  size="36"
                  :color="categoria.completada ? 'green darken-3' : '#222'"
                  class="border-check transition-all"
                >
                  <v-icon :color="categoria.completada ? 'white' : '#444'" small>
                    {{ categoria.completada ? "mdi-check-bold" : "mdi-circle-outline" }}
                  </v-icon>
                </v-avatar>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title
                  class="text-h6 font-weight-bold transition-all"
                  :class="{ 'grey--text text--darken-1': categoria.completada }"
                >
                  {{ categoria.nombre }}
                </v-list-item-title>
                <v-list-item-subtitle
                  class="text-caption font-weight-medium"
                  :class="categoria.completada ? 'green--text text--lighten-1' : 'grey--text'"
                >
                  <v-icon x-small :color="categoria.completada ? 'green' : 'grey'" class="mr-1">
                    {{ categoria.completada ? "mdi-check-all" : "mdi-package-variant" }}
                  </v-icon>
                  {{ categoria.completada ? "Completado" : `${categoria.productos_count} productos pendientes` }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn icon small>
                  <v-icon color="grey darken-3">mdi-chevron-right</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider
              v-if="index < categorias.length - 1"
              :key="'div' + index"
              class="grey darken-4"
            ></v-divider>
          </template>
        </v-list>
      </div>

      <div :class="{ 'sticky-bottom-mobile': $vuetify.breakpoint.xs }">
        <v-btn
          x-large
          block
          rounded-pill
          :color="isCategoriasCompletadas ? 'white' : '#1e1e1e'"
          class="font-weight-black py-7 py-sm-8"
          :class="isCategoriasCompletadas ? 'black--text' : 'grey--text'"
          :disabled="!isCategoriasCompletadas"
          @click="goToFinalizar"
        >
          <v-icon left size="20">mdi-flag-checkered</v-icon>
          FINALIZAR INVENTARIO
        </v-btn>
      </div>

      <v-dialog v-model="modalEliminar" max-width="550" persistent overlay-color="black" overlay-opacity="0.9">
        <v-card class="bg-dark-dialog rounded-xl pa-4 border-subtle">
          <v-card-title class="text-h5 font-weight-black white--text px-6 pt-6">
            ¿Borrar definitivamente?
          </v-card-title>

          <v-card-text class="px-6">
            <p class="grey--text text-body-2 mb-6">
              Esta acción eliminará todo el progreso actual de este inventario.
            </p>

            <div class="confirm-check-box pa-4 rounded-lg d-flex align-center">
              <v-checkbox
                v-model="confirmacionBorrado"
                dark
                hide-details
                color="white"
                class="mt-0 pt-0"
              ></v-checkbox>
              <span class="white--text text-caption font-weight-bold">
                Comprendo que esta acción es permanente y no se puede deshacer.
              </span>
            </div>
          </v-card-text>

          <v-card-actions class="pb-6 px-6 justify-end">
            <v-btn text color="grey" class="font-weight-bold px-6" @click="modalEliminar = false">
              CANCELAR
            </v-btn>
            <v-btn
              color="white"
              class="black--text font-weight-bold rounded-pill px-8 ml-2"
              :disabled="!confirmacionBorrado"
              @click="eliminarInventario"
            >
              BORRAR DEFINITIVAMENTE
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import inventarioMixin from "@/mixins/inventario.mixin";
import InventarioAPI from "@/api/inventario";
import moment from "moment";

export default {
  mixins: [inventarioMixin],
  name: "SucursalProductosInventarioIniciado",
  data() {
    return {
      categorias: [],
      inventario: null,
      modalEliminar: false,
      confirmacionBorrado: false,
      descargandoHoja: false,
      moment: moment
    };
  },
  computed: {
    isCategoriasCompletadas() {
      return this.categorias.length > 0 && this.categorias.every((cat) => cat.completada);
    },
    progresoPorcentaje() {
      if (!this.categorias.length) return 0;
      const completas = this.categorias.filter(c => c.completada).length;
      return Math.round((completas / this.categorias.length) * 100);
    }
  },
  methods: {
    irCategoria(categoria) {
      this.$router.push({
        name: this.rutaInventario('categoria'),
        params: { 
          inventarioUuid: this.$route.params.inventarioUuid,
          categoriaId: categoria.id 
        },
      });
    },
    getInventarioUuid() {
      const uuid = this.$route.params.inventarioUuid;
      InventarioAPI.get(uuid)
        .then((response) => {
          this.inventario = response.data;
          this.categorias = response.data.categorias || [];
        })
        .catch(console.error);
    },
    goToFinalizar() {
      this.$router.push({
        name: this.rutaInventario('finalizar'),
        params: { inventarioUuid: this.inventario.uuid },
      });
    },
    async descargarHojaConteo() {
      this.descargandoHoja = true;
      try {
        const response = await InventarioAPI.descargarHojaConteo(this.inventario.uuid);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `hoja-conteo-${moment(this.inventario.fecha_inicio).format('YYYY-MM-DD')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error(err);
      } finally {
        this.descargandoHoja = false;
      }
    },
    abrirModalEliminar() {
      this.confirmacionBorrado = false;
      this.modalEliminar = true;
    },
    eliminarInventario() {
      InventarioAPI.delete(this.inventario.uuid)
        .then(() => {
          this.modalEliminar = false;
          this.$router.push({ name: this.rutaInventario('stock') });
        })
        .catch(console.error);
    }
  },
  created() {
    this.getInventarioUuid();
  },
};
</script>

<style scoped>
.bg-dark-refined { background-color: #161616; }
.bg-dark-dialog { background-color: #0f0f0f !important; }
.border-subtle { border: 1px solid #252525 !important; }
.tracking-tighter { letter-spacing: -1.5px !important; }
.leading-none { line-height: 1 !important; }

.item-done-bg { background-color: rgba(76, 175, 80, 0.05) !important; }

.yt-task-item {
  border-left: 4px solid transparent;
  transition: all 0.2s ease;
}
.yt-task-item:hover {
  background-color: #1a1a1a !important;
  border-left: 4px solid #2196F3;
}

.confirm-check-box {
  background-color: #1a1a1a;
  border: 1px solid #333;
}

.pulse-blue {
  width: 8px;
  height: 8px;
  background-color: #2196F3;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(33, 150, 243, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
}

@media (max-width: 600px) {
  .sticky-bottom-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background: linear-gradient(transparent, #0f0f0f 40%);
    z-index: 10;
  }
}
</style>